import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { services } from '../mock/data';
import { PageHero } from '../components/motion/PageHero';
import { Reveal, RevealText } from '../components/motion/Reveal';
import { TiltCard } from '../components/motion/TiltCard';
import { useMotionMode } from '../contexts/MotionContext';

const WHY = [
  { title: 'Professional Chauffeurs', description: 'Experienced, vetted, and trained to the highest standards' },
  { title: 'Luxury Fleet', description: 'Mercedes V-Class vehicles with premium amenities' },
  { title: '24/7 Availability', description: 'Around-the-clock service for your convenience' },
  { title: 'Nationwide Coverage', description: 'Based in Cheshire, serving all of the UK' },
];

const ServiceCard = ({ service, index, tilt }) => (
  <Reveal delay={(index % 3) * 0.12}>
    <Link to={`/services/${service.slug}`} data-testid={`services-card-${service.slug}`}>
      <TiltCard enabled={tilt} className="group overflow-hidden bg-white/5 border border-white/10 hover:border-white/30 transition-colors duration-500 h-full">
        <div className="aspect-[4/3] overflow-hidden relative">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <div className="text-xs font-mono tracking-[0.2em] text-white/60 mb-1">SERVICE {String(index + 1).padStart(2, '0')}</div>
            <h3 className="text-2xl font-bold">{service.title}</h3>
          </div>
        </div>
        <div className="p-6">
          <p className="text-white/70 mb-4 line-clamp-3">{service.description}</p>
          <div className="flex items-center text-white">
            <span className="text-sm font-medium">View Details</span>
            <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-2" />
          </div>
        </div>
      </TiltCard>
    </Link>
  </Reveal>
);

const Services = () => {
  const { cinematic } = useMotionMode();
  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <PageHero
        image="https://static.prod-images.emergentagent.com/jobs/78f5a82c-442c-401f-97ed-118ff3b2b080/images/a679eb9da303fba429277a841fd08b55a184560d0abeaba31976fea390758984.png"
        eyebrow="What we do"
        title="Our Services"
        subtitle="Comprehensive chauffeur solutions designed to exceed your expectations"
      />

      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" style={{ perspective: 1200 }}>
          {services.map((s, i) => <ServiceCard key={s.id} service={s} index={i} tilt={cinematic} />)}
        </div>
      </section>

      <section className="py-24 bg-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4"><RevealText text="Why Choose Obsidian?" /></h2>
            <Reveal className="text-xl text-white/60 max-w-2xl mx-auto">Excellence is not just a promise, it's our standard</Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {WHY.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.1}>
                <div className="bg-black border border-white/10 hover:border-white/30 p-8 text-center h-full transition-colors duration-500">
                  <div className="text-5xl font-bold text-transparent [-webkit-text-stroke:1px_rgba(255,255,255,0.35)] mb-4">{String(i + 1).padStart(2, '0')}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-white/60 text-sm">{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6"><RevealText text="Ready to Book?" /></h2>
          <Reveal className="text-xl text-white/60 mb-12">Get in touch today for a personalised quote</Reveal>
          <Reveal delay={0.2}>
            <Link to="/booking" data-testid="services-cta-quote-btn" className="group bg-white text-black px-8 py-4 text-lg font-medium hover:bg-white/90 transition-colors duration-300 inline-flex items-center">
              Request a Quote <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Services;
